// Replacement or refund on the step (2026-10-27, docs/contact-view.md
// "Replacement or refund on the step"): drawn at the top of the replacement
// or refund step on the agent draft panel and in the admin agent column.
//   - A part out of stock: the warning, in the warning color, and "Switch to
//     refund" (and, when only some parts are out, "Refund the out-of-stock
//     part(s), replace the rest"). The step stays as staged.
//   - The customer did not say which they want: "Resolution: Replacement |
//     Refund", the agent's default selected.
//   - Switched with no toggle: a link back to the proposed path.
// onChoose(path, why).

import { Box, Button, Link, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { RESOLUTION_REPLACEMENT, RESOLUTION_REFUND, RESOLUTION_SPLIT, SWITCH_TO_REFUND_LABEL } from '@cxhub/shared'
import { resolutionLabel, resolutionWhyFor } from './resolutionSwitch.js'

export default function ResolutionBar({ view, onChoose, disabled = false, testId = 'resolution-bar' }) {
  if (!view) return null
  const { path, headPath, options = [], toggle, stockLines = [], customerQuote = null, note = null } = view
  const has = (v) => options.some((o) => o.value === v)
  const choose = (to) => {
    if (!to || to === path || disabled) return
    onChoose?.(to, resolutionWhyFor({ stockLines, to }))
  }
  const toggleValue = path === RESOLUTION_SPLIT ? null : path
  return (
    <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mb: '4px' }}>
      {stockLines.length > 0 && (
        <Box data-testid="resolution-stock-warning" sx={{ border: 1, borderColor: 'warning.main', bgcolor: 'rgba(237, 108, 2, 0.08)', borderRadius: '4px', px: '8px', py: '6px' }}>
          {stockLines.map((line) => (
            <Typography key={line} sx={{ fontSize: 13, fontWeight: 600, color: 'warning.dark' }}>{line}</Typography>
          ))}
          {path === RESOLUTION_REPLACEMENT && (
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '4px' }}>
              {has(RESOLUTION_REFUND) && (
                <Button size="small" variant="outlined" color="warning" disabled={disabled} data-testid="resolution-switch-refund"
                  onClick={() => choose(RESOLUTION_REFUND)} sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}>
                  {SWITCH_TO_REFUND_LABEL}
                </Button>
              )}
              {has(RESOLUTION_SPLIT) && (
                <Button size="small" variant="outlined" color="warning" disabled={disabled} data-testid="resolution-switch-split"
                  onClick={() => choose(RESOLUTION_SPLIT)} sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}>
                  {resolutionLabel(RESOLUTION_SPLIT)}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}
      {note && <Typography data-testid="resolution-note" sx={{ fontSize: 12, color: 'text.secondary' }}>{note}</Typography>}
      {toggle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Resolution:</Typography>
          <ToggleButtonGroup size="small" exclusive value={toggleValue} disabled={disabled} data-testid="resolution-toggle"
            onChange={(_e, v) => choose(v)} aria-label="Resolution">
            {[RESOLUTION_REPLACEMENT, RESOLUTION_REFUND].filter(has).map((v) => (
              <ToggleButton key={v} value={v} data-testid={`resolution-toggle-${v}`} sx={{ textTransform: 'none', fontSize: 12, py: 0.25, px: 1.25 }}>
                {resolutionLabel(v)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      {customerQuote && (
        <Typography data-testid="resolution-customer-quote" sx={{ fontSize: 12, color: 'text.secondary' }}>Customer asked: &ldquo;{customerQuote}&rdquo;</Typography>
      )}
      {path !== headPath && (!toggle || path === RESOLUTION_SPLIT) && (
        <Link component="button" type="button" underline="hover" disabled={disabled} data-testid="resolution-switch-back"
          onClick={() => choose(headPath)} sx={{ fontSize: 12, alignSelf: 'flex-start' }}>
          Switch back to the {resolutionLabel(headPath).toLowerCase()}
        </Link>
      )}
    </Box>
  )
}
